import type { UserData } from "@/components/Forms/AddNoteDialog.tsx";
import {
    Autocomplete,
    Box,
    Chip,
    FormControl,
    FormControlLabel,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import { Bell } from "lucide-react";
import { RhfCheckbox, RhfTextField } from "mui-rhf-integration";
import type React from "react";
import { useFormContext } from "react-hook-form";

interface NotificationSectionProps {
    availableUsers: UserData[];
}

const PRIORITY_OPTIONS = [
    { value: "low", label: "Low", color: "#4caf50" },
    { value: "medium", label: "Medium", color: "#ff9800" },
    { value: "high", label: "High", color: "#f44336" },
];

const AddNoteNotificationSection: React.FC<NotificationSectionProps> = ({ availableUsers }) => {
    const form = useFormContext();
    const enabled = form.watch("enabled");
    const notifyUsers = form.watch("notifyUsers") ?? [];

    const handleUserSelection = (userIds: string[]) => {
        form.setValue("notifyUsers", userIds);
    };

    const getAvailableUsers = () => {
        return availableUsers.map((user) => ({
            id: user.id,
            label: `${user.firstName} ${user.lastName} (${user.role})`,
        }));
    };

    const getSelectedUsers = () => {
        return getAvailableUsers().filter((user) => notifyUsers.includes(user.id));
    };

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: "background.default",
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                }}
            >
                <Bell size={20} />
                <Typography
                    sx={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        color: "text.primary",
                    }}
                >
                    Notifications
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <FormControlLabel
                    control={<RhfCheckbox control={form.control} name="enabled" />}
                    label="Send notification to users"
                    sx={{ mb: 2 }}
                />
            </Box>

            {enabled && (
                <>
                    <RhfTextField
                        name="title"
                        control={form.control}
                        label="Notification Title"
                        placeholder={"Enter notification title..."}
                        required
                        sx={{
                            mb: 3,
                            width: "100%",
                        }}
                    />

                    <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
                        <FormControl sx={{ minWidth: 120 }}>
                            <RhfTextField
                                select
                                control={form.control}
                                name="priority"
                                label="Priority"
                            >
                                {PRIORITY_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: "50%",
                                                    backgroundColor: option.color,
                                                }}
                                            />
                                            {option.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </RhfTextField>
                        </FormControl>

                        <FormControlLabel
                            control={<RhfCheckbox control={form.control} name="sendEmail" />}
                            label="Send Email"
                        />
                    </Box>

                    <Autocomplete
                        multiple
                        options={getAvailableUsers()}
                        value={getSelectedUsers()}
                        onChange={(_event, newValue) => {
                            handleUserSelection(newValue.map((item) => item.id));
                        }}
                        getOptionLabel={(option) => option.label}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    variant="outlined"
                                    label={option.label}
                                    {...getTagProps({ index })}
                                    key={option.id}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Notify Users"
                                placeholder="Select users to notify..."
                                error={!!form.formState.errors.notifyUsers}
                                helperText={form.formState.errors.notifyUsers?.message as string}
                            />
                        )}
                        sx={{ mb: 1 }}
                    />
                </>
            )}
        </Box>
    );
};

export default AddNoteNotificationSection;
